import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listRisk, getRiskTypes, getRiskStats, schvalitRisk, skrytRisk } from "../lib/api.js";
import type { RiskListItem, RiskStatsResponse } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, HashIcon, AlertIcon } from "../lib/icons.js";
import { FotkaVyrobce } from "./MotorexPage.js";

/**
 * RISK RACING — co výrobce vede × co z toho vedeme my.
 *
 * 🔑 Produkt se pouští na eshop nebo schovává PŘÍMO Z DLAŽDICE (od 24. 8.
 * 2026; do té doby jen na detailu `/risk/:sku`, což znamenalo otevřít kartu
 * kvůli jednomu kliknutí). Sortiment a ceny pořád určuje ABRA, obsah vozí
 * `risk_zapis.py` a zakládá `risk_zalozeni.py` (oba `ktm-vlasim`).
 *
 * 🇨🇿 Názvy jsou ČESKY, když je produkt na eshopu — `shopifyTitle` je otisk
 * karty, plní ho noční `shopify-sync-back.ts`. Dokud produkt na eshopu není,
 * ukáže se jméno od výrobce (anglicky) a to je záměr: je to jediné jméno,
 * které o něm zatím existuje.
 *
 * Zdroje jsou dva a je to vidět i tady:
 *   • velkoobchod GOMX365 — nákupka a dostupnost
 *   • výrobce riskracing.com — popisy, fotky, videa, jeho cena
 *
 * 🔑 Párování na ABRU je RUČNÍ (`mapa.json`). Naše kódy `00103`–`00120`
 * kolidují s SKU výrobce a automatická shoda je 7× ze 7 špatně, proto se
 * tu nic neodvozuje — `abraCode` je hotový sloupec z importu.
 *
 * ⛔ Ceny u dodavatele jsou NÁKUPNÍ (bez DPH, reverse charge). „Naše" je
 * doporučená cena z klíče — nákup × doprava ÷ marže × DPH.
 */

type Filtr = "vse" | "vedeme" | "nevedeme";

export function RiskPage(): JSX.Element {
  const [typy, setTypy] = useState<Array<{ name: string; count: number }> | null>(null);
  const [stats, setStats] = useState<RiskStatsResponse | null>(null);
  const [filtr, setFiltr] = useState<Filtr>("vse");
  const [typ, setTyp] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<RiskListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  const [hledej, setHledej] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setHledej(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setOffset(0), [filtr, typ, hledej]);

  const obnovStats = useCallback(
    () => getRiskStats().then(setStats).catch(() => setStats(null)),
    [],
  );

  useEffect(() => {
    getRiskTypes().then((d) => setTypy(d.types)).catch(() => setTypy([]));
    void obnovStats();
  }, [obnovStats]);

  const nacti = useCallback(() => {
    setLoading(true);
    return listRisk({
      type: typ ?? undefined,
      search: hledej || undefined,
      stocked: filtr === "vse" ? undefined : filtr,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [typ, hledej, filtr, offset]);

  useEffect(() => {
    void nacti();
  }, [nacti]);

  if (!typy) return <LoadingBlock label="Načítám katalog…" />;
  const p = stats?.prehled;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">RISK RACING</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Co výrobce vede a co z toho vedeme my. <strong>Sortiment i ceny určuje ABRA</strong> —
          tenhle výpis je zrcadlo dvou zdrojů: velkoobchodu GOMX365 (nákupka, dostupnost)
          a výrobce riskracing.com (popisy, fotky, videa).
        </p>
      </header>

      <div className="mb-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Dlazdice label="Výrobce vede" hodnota={p?.vKatalogu ?? 0}
                  popis="variant u velkoobchodu" />
        <Dlazdice label="Z toho vedeme my" hodnota={p?.vedeme ?? 0} tón="green"
                  popis="spárováno na kartu v ABŘE" onClick={() => setFiltr("vedeme")} />
        <Dlazdice label="Mohli bychom začít" hodnota={p?.nevedeme ?? 0} tón="amber"
                  popis="výrobce to má, my kartu nemáme" onClick={() => setFiltr("nevedeme")} />
        <Dlazdice label="Na eshopu" hodnota={p?.naEshopu ?? 0}
                  popis={p?.cekaNaRobota
                    ? `+ ${p.cekaNaRobota} čeká na robota (jede každých 15 min)`
                    : `skladem u dodavatele ${p?.skladem ?? 0}`} />
      </div>

      {/* Poctivost: co tenhle zdroj neumí */}
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertIcon size={18} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong>Počet kusů u dodavatele neznáme</strong> — velkoobchod ani výrobce ho
          nedávají, jen „skladem / vyprodáno". Proti Motorexu je to krok zpět, tam stránka
          výrobce vypisuje přesné číslo.
          {p && p.zmizeloAVedeme > 0 && (
            <>
              {" "}<strong>{p.zmizeloAVedeme}×</strong> navíc zmizelo z feedu, přestože to
              vedeme — může to znamenat, že to výrobce ruší.
            </>
          )}
        </div>
      </div>

      {stats?.lastRun && (
        <p className="mb-4 text-xs text-zinc-400">
          Poslední import {new Date(stats.lastRun.startedAt).toLocaleString("cs-CZ")} —
          velkoobchod {stats.lastRun.productsSupplier}, výrobce{" "}
          {stats.lastRun.productsManufacturer}, spárováno {stats.lastRun.productsMatched},
          nových {stats.lastRun.productsNew}, změněných {stats.lastRun.productsUpdated}
          {stats.lastRun.productsVanished > 0
            ? `, zmizelo ${stats.lastRun.productsVanished}`
            : ""}.
          {p && ` Popis od výrobce má ${p.popisOdVyrobce}, video ${p.sVideem}.`}
        </p>
      )}

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {([
          { k: "vse" as Filtr, l: "Vše", n: p?.vKatalogu },
          { k: "vedeme" as Filtr, l: "Vedeme", n: p?.vedeme },
          { k: "nevedeme" as Filtr, l: "Nevedeme", n: p?.nevedeme },
        ]).map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFiltr(f.k)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
              filtr === f.k
                ? "bg-ktm-orange text-white border-ktm-orange"
                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
            }`}
          >
            <span className="font-medium">{f.l}</span>
            {f.n != null && (
              <span className={`ml-2 text-xs ${filtr === f.k ? "text-white/80" : "text-zinc-400"}`}>
                {f.n}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat název, SKU, EAN nebo kód ABRA…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-80"
          />
        </div>
        <select
          value={typ ?? ""}
          onChange={(e) => setTyp(e.target.value || null)}
          className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white max-w-xs"
        >
          <option value="">Všechny typy</option>
          {typy.map((t) => (
            <option key={t.name} value={t.name}>{t.name} ({t.count})</option>
          ))}
        </select>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState icon={<PackageIcon size={28} />} title="Žádné výsledky"
                    description="Uprav filtr nebo hledání." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Po kliknutí na tlačítko se výpis načte znovu — ať štítek
              „Čeká na robota" naskočí hned a nemusí se mačkat F5. */}
          {items.map((it) => (
            <Karta key={it.id} item={it} onZmena={() => { void nacti(); void obnovStats(); }} />
          ))}
        </div>
      )}

      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button size="sm" variant="secondary" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}>← Předchozí</Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z {total.toLocaleString("cs-CZ")}
          </div>
          <Button size="sm" variant="secondary" disabled={offset + LIMIT >= total}
                  onClick={() => setOffset(offset + LIMIT)}>Další →</Button>
        </div>
      )}
    </div>
  );
}

function Dlazdice({
  label, hodnota, tón = "neutral", popis, onClick,
}: {
  label: string;
  hodnota: number;
  tón?: "neutral" | "green" | "amber";
  popis?: string;
  onClick?: () => void;
}): JSX.Element {
  const barva = tón === "green" ? "text-emerald-700"
    : tón === "amber" ? "text-amber-700" : "text-zinc-900";
  const obsah = (
    <>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`mt-0.5 text-2xl font-semibold ${barva}`}>
        {hodnota.toLocaleString("cs-CZ")}
      </div>
      {popis && <div className="mt-1 text-[11px] leading-snug text-zinc-400">{popis}</div>}
    </>
  );
  const zaklad = "rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left";
  return onClick
    ? <button type="button" onClick={onClick}
              className={`${zaklad} transition-colors hover:border-ktm-orange`}>{obsah}</button>
    : <div className={zaklad}>{obsah}</div>;
}

/**
 * Dlaždice produktu.
 *
 * 🔑 Od 24. 8. 2026 se z ní dá produkt pustit na eshop nebo schovat, bez
 * otevírání detailu. Vít: *„možná by bylo fajn mít tlačítko už na té dlaždici
 * dát na eshop nebo skrýt z eshopu a nemusím furt otevírat karty."*
 *
 * ⛔⛔ Proto to UŽ NENÍ jeden velký `<Link>` kolem celé karty. Tlačítko uvnitř
 * odkazu je neplatné HTML a klik by spustil obojí naráz — odkaz vede jen přes
 * fotku a název, tlačítko je vedle něj.
 *
 * ⛔ Zakládací logika tu není. „Schválit" zapíše jen příznak, produkt zakládá
 * robot do 15 minut (`risk_zalozeni.py` v repu `ktm-vlasim`).
 */
function Karta({ item, onZmena }: {
  item: RiskListItem;
  onZmena: () => void;
}): JSX.Element {
  const [pracuje, setPracuje] = useState<null | "schvalit" | "skryt">(null);
  const [chyba, setChyba] = useState<string | null>(null);

  const naEshopu = item.eshopStav === "na-eshopu";
  const ceka = item.eshopStav === "ceka";
  const skryto = item.eshopStav === "skryto";

  async function akce(co: "schvalit" | "skryt"): Promise<void> {
    setPracuje(co);
    setChyba(null);
    try {
      await (co === "schvalit" ? schvalitRisk(item.sku) : skrytRisk(item.sku));
      onZmena();
    } catch (e) {
      setChyba(e instanceof Error ? e.message : "nepovedlo se");
    } finally {
      setPracuje(null);
    }
  }

  return (
    <div className="group bg-white rounded-2xl border border-zinc-200 hover:border-ktm-orange hover:shadow-md overflow-hidden transition-all flex flex-col">
      <Link to={`/risk/${encodeURIComponent(item.sku)}`} className="block">
        <div className="aspect-square bg-zinc-50 relative overflow-hidden">
          <FotkaVyrobce src={item.imgUrl} alt={item.name}
                        className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform" />
          <div className="absolute bottom-2 left-2 flex gap-1 flex-wrap">
            <Badge tone={item.stocked ? "green" : "amber"}>
              {item.stocked ? "Vedeme" : "Nevedeme"}
            </Badge>
            {/* Stav vůči eshopu — ať se kvůli němu nemusí otevírat detail. */}
            {naEshopu && <Badge tone="green">Na eshopu</Badge>}
            {ceka && <Badge tone="blue">Čeká na robota</Badge>}
            {item.eshopStav === "chyba" && <Badge tone="red">Zakládání selhalo</Badge>}
            {skryto && <Badge tone="neutral">Skryto</Badge>}
            {!item.available && <Badge tone="neutral">Vyprodáno</Badge>}
          </div>
          {(item.fotek > 1 || item.videi > 0) && (
            <div className="absolute top-2 right-2 rounded-md bg-white/90 border border-zinc-200 px-1.5 py-0.5 text-[11px] text-zinc-600">
              {item.fotek}× foto{item.videi > 0 ? ` · ${item.videi}× video` : ""}
            </div>
          )}
        </div>

        <div className="px-3 pt-3">
          <div className="text-xs text-zinc-400 font-mono truncate">
            {item.sku}
            {item.productType && (
              <><span className="text-zinc-300"> · </span><span>{item.productType}</span></>
            )}
          </div>
          <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 min-h-[2.5rem]">
            {item.name}
          </div>
          {/* ⚠ Jméno u výrobce jen tehdy, když se od českého LIŠÍ — jinak by
              u poloviny karet stál dvakrát tentýž řádek. */}
          {item.nameVyrobce && item.nameVyrobce !== item.name && (
            <div className="text-[11px] text-zinc-400 truncate" title={item.nameVyrobce}>
              u výrobce: {item.nameVyrobce}
            </div>
          )}
        </div>
      </Link>

      <div className="px-3 pb-3 pt-2 mt-auto">
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-zinc-900">
              {item.suggestedPrice != null
                ? `${item.suggestedPrice.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč`
                : "—"}
              <span className="ml-1 text-[11px] font-normal text-zinc-500">naše</span>
            </div>
            <div className="text-[11px] text-zinc-400">
              nákup {item.purchasePrice?.toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) ?? "—"}
              {item.manufacturerPrice != null && (
                <> · výrobce {item.manufacturerPrice.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })}</>
              )}
            </div>
          </div>
          {item.abraCode && (
            <div className="text-[11px] text-right text-zinc-500 font-mono">{item.abraCode}</div>
          )}
        </div>

        {/* ⚠ `min-h-11` = 44 px, doporučená klikací plocha na dotykovém displeji. */}
        <div className="mt-2">
          {ceka ? (
            <div className="min-h-11 flex items-center justify-center rounded-lg bg-blue-50 text-blue-700 text-xs px-2 text-center">
              Čeká na robota — jede každých 15 min
            </div>
          ) : naEshopu ? (
            <button
              type="button"
              disabled={pracuje !== null}
              onClick={() => void akce("skryt")}
              className="w-full min-h-11 rounded-lg border border-zinc-200 text-sm text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 active:bg-zinc-100 disabled:opacity-50 transition-colors"
            >
              {pracuje === "skryt" ? "Skrývám…" : "Skrýt z eshopu"}
            </button>
          ) : (
            <button
              type="button"
              disabled={pracuje !== null}
              onClick={() => void akce("schvalit")}
              className="w-full min-h-11 rounded-lg bg-ktm-orange text-white text-sm font-medium hover:brightness-95 active:brightness-90 disabled:opacity-50 transition-all"
            >
              {pracuje === "schvalit"
                ? "Schvaluji…"
                : skryto ? "Vrátit na eshop" : "Dát na eshop"}
            </button>
          )}
          {chyba && (
            <div className="mt-1.5 text-[11px] leading-snug text-red-600">{chyba}</div>
          )}
        </div>
      </div>
    </div>
  );
}
